// activityViewModel.js — m3-06a "build once, render twice" view model (pure, node-testable)
//
// THE CONTINUITY CONTRACT (the gate this slice carries):
// The Activity surface (Project Board ⇄ Triggered Tasks) is rendered in TWO placements:
//   - Standard mode  : full-page in the content area.
//   - Agent mode     : inside the resizable right panel, menu-less.
// It MUST be built from ONE source so the two placements can never drift. This module is
// that single source: `buildActivityViewModel()` returns the descriptor (which tab is
// active, the props each placement needs, the placement flag). Both placements call THIS
// function and only this function — the gate asserts a single caller (the milestone-1
// parity-seam pattern, GATES.md §3.3).
//
// It does NOT render. It returns a plain descriptor object the React shell maps to JSX.
// Pure: no React, no DOM — runs headless under node.

import { resolveActivityView } from "./modeResolver.js"

export const PLACEMENTS = Object.freeze(["page", "panel"])

// Build the Activity surface descriptor.
//
// @param opts.activeView   — requested tab ('board' | 'agents'); resolved with `stored`.
// @param opts.storedView   — persisted tab (localStorage) used as fallback.
// @param opts.placement    — 'page' (Standard full-page) | 'panel' (Agent right panel).
// @param opts.companySlug  — tenant slug threaded to every data fetch (tenant boundary).
// @param opts.litTask      — the active board→chat task reference id (m3-15), or null.
//
// Returns a STABLE descriptor whose `surface`/`props` are identical for both placements
// given the same inputs — that identity IS the continuity contract.
export function buildActivityViewModel(opts = {}) {
  const {
    activeView,
    storedView = null,
    placement = "page",
    companySlug = null,
    litTask = null,
    onAsk = null,
    seedJob = null,
  } = opts

  const view = resolveActivityView(activeView, storedView)
  const inPanel = placement === "panel"

  // The tab definitions — identical in both placements. Internal id 'agents' renders the
  // "Agent Jobs" label (TRIGGERED-SPEC-v2 §0 — LOCKED naming decision).
  const tabs = [
    { id: "board", label: "Project Board", icon: "activity" },
    { id: "agents", label: "Agent Jobs", icon: "robot" },
  ]

  // The props the active surface needs. These are placement-INVARIANT (same for page+panel)
  // EXCEPT the cosmetic `compact` flag — the data contract (slug, view, litTask) is shared.
  const surfaceProps = {
    companySlug,
    litTask,
    onAsk,
    view,
    // Task 9: a cross-page library launch (job) rides through to TriggeredTasksList.seedJob.
    seedJob,
  }

  return {
    view, // resolved active tab
    tabs,
    placement,
    inPanel,
    // `surfaceKey` keys the swap animation AND proves single-source: both placements pass it.
    surfaceKey: `activity-${view}`,
    surface: view === "board" ? "ProjectBoard" : "TriggeredTasks",
    surfaceProps,
    // Cosmetic-only placement adjustments (never affect data/tenant scope).
    chrome: {
      compact: inPanel,        // the panel hides its own menu; tabs stay
      stickyTabs: !inPanel,    // page gets the sticky blurred segmented control
      showShellNav: !inPanel,  // the gradient nav only shows in Standard page mode
    },
  }
}

// Equality check the gate uses to prove the two placements share one data contract:
// the surface + the tenant/data props must be identical; only `chrome` differs.
export function sameDataContract(vmA, vmB) {
  if (!vmA || !vmB) return false
  if (vmA.surface !== vmB.surface) return false
  if (vmA.view !== vmB.view) return false
  const a = vmA.surfaceProps || {}
  const b = vmB.surfaceProps || {}
  return (
    a.companySlug === b.companySlug &&
    a.litTask === b.litTask &&
    a.view === b.view
  )
}
